// @ts-check
// Regras puras do mapa de calor dos leads (contrato 07, seção 9): nível pelo caminho escolhido (Brasil, UF, cidade),
// áreas do nível, ranking, faixas da legenda e linhas da planilha. A tela fica em
// client/src/modules/dashboards/geo-map-widget.js.
import { BRAZIL_STATES } from '../../../shared/geo/brazil-states.js';
import { computeColorBreaks, geoMetricValue, isRateMetric, rankAreas } from '../../../shared/geo/geo-metrics.js';

/**
 * @typedef {{ leads: number, withVisit: number | null, enrollments: number, visitRate?: number | null, conversionRate?: number | null }} GeoCounts
 * @typedef {GeoCounts & { code: string, key: string, label: string }} GeoStateArea
 * @typedef {GeoCounts & { code: string, stateCode: string, label: string }} GeoCityArea
 * @typedef {GeoCounts & { key: string, cityCode: string, label: string | null }} GeoNeighborhoodArea
 * @typedef {GeoCounts & { postcode: string, cityCode: string | null, neighborhoodKey?: string | null, latitude: number, longitude: number }} GeoPoint
 * @typedef {{ kind: 'GEO_MAP', report: string, metric: string, totals: GeoCounts,
 *   quality: { total: number, located: number, withoutPostcode: number, invalid: number, pending: number },
 *   qualityHouseholds?: { withoutPostcode: QualityHouseholds, invalid: QualityHouseholds },
 *   summary?: Record<string, number>, states: GeoStateArea[], cities: GeoCityArea[], neighborhoods: GeoNeighborhoodArea[], points: GeoPoint[] }} GeoMapData
 * @typedef {{ stateCode: string | null, cityCode: string | null }} GeoPath
 * @typedef {'STATES' | 'CITIES' | 'NEIGHBORHOODS'} GeoLevel
 * @typedef {GeoCounts & { id: string, code: string | null, label: string, isUnnamed?: boolean }} GeoArea
 * @typedef {{ count: number, ids: string[], leadsWithoutHousehold: number }} QualityHouseholds famílias distintas (até 50 ids)
 */

export const EMPTY_GEO_PATH = Object.freeze({ stateCode: null, cityCode: null });
export const GEO_RANKING_LIMIT = 10;
export const GEO_NEIGHBORHOOD_RANKING_LIMIT = 15;

/** @param {GeoPath} path @returns {GeoLevel} */
export const geoLevelOf = (path) => (path.cityCode ? 'NEIGHBORHOODS' : path.stateCode ? 'CITIES' : 'STATES');

/** Malha a pedir para o nível: o país por UF, os municípios da UF ou, no nível de bairro, o contorno da cidade. @param {GeoPath} path */
export const meshCodeOf = (path) => path.cityCode ?? path.stateCode ?? 'BR';

/**
 * Áreas do nível atual com as contagens, já no formato do ranking.
 * @param {GeoMapData} data @param {GeoPath} path
 * @returns {GeoArea[]}
 */
export function geoAreasFor(data, path) {
  const level = geoLevelOf(path);
  if (level === 'STATES') return data.states.map((state) => ({ ...state, id: state.code, code: state.code, label: state.label }));
  if (level === 'CITIES') {
    return data.cities.filter((city) => city.stateCode === path.stateCode).map((city) => ({ ...city, id: city.code, code: city.code, label: city.label }));
  }
  return data.neighborhoods.filter((neighborhood) => neighborhood.cityCode === path.cityCode)
    .map((neighborhood) => ({ ...neighborhood, id: neighborhood.key, code: null, label: neighborhood.label ?? '', isUnnamed: !neighborhood.label }));
}

/**
 * Trilha de volta: Brasil, UF e cidade (só os níveis já escolhidos).
 * @param {GeoMapData} data @param {GeoPath} path
 * @returns {Array<{ key: string, label: string | null, path: GeoPath }>}
 */
export function geoBreadcrumb(data, path) {
  /** @type {Array<{ key: string, label: string | null, path: GeoPath }>} */
  const items = [{ key: 'BR', label: null, path: EMPTY_GEO_PATH }];
  if (path.stateCode) {
    const state = BRAZIL_STATES.find((candidate) => candidate.code === path.stateCode);
    items.push({ key: path.stateCode, label: state?.name ?? path.stateCode, path: { stateCode: path.stateCode, cityCode: null } });
  }
  if (path.cityCode) {
    const city = data.cities.find((candidate) => candidate.code === path.cityCode);
    items.push({ key: path.cityCode, label: city?.label ?? path.cityCode, path });
  }
  return items;
}

/**
 * Próximo caminho ao clicar numa área: UF desce para municípios, município desce para bairros; bairro não desce.
 * @param {GeoPath} path @param {string | null} code
 * @returns {GeoPath | null}
 */
export function drillInto(path, code) {
  if (!code) return null;
  const level = geoLevelOf(path);
  if (level === 'STATES') return { stateCode: code, cityCode: null };
  if (level === 'CITIES') return { stateCode: path.stateCode, cityCode: code };
  return null;
}

/**
 * Escala do nível: limites das classes e o valor de cada área. Taxa só conta área com leads.
 * @param {GeoArea[]} areas @param {string} metric
 */
export function geoScaleFor(areas, metric) {
  const values = new Map(areas.map((area) => [area.id, geoMetricValue(area, metric)]));
  return { values, breaks: computeColorBreaks([...values.values()]) };
}

/**
 * Ranking ao lado do mapa: maiores na métrica; em taxa, só áreas com leads.
 * @param {GeoArea[]} areas @param {string} metric @param {number} limit
 */
export function geoRanking(areas, metric, limit) {
  const ranked = rankAreas(areas.filter((area) => area.leads > 0), metric);
  return { items: ranked.slice(0, limit), others: Math.max(0, ranked.length - limit) };
}

/**
 * Lista de um objeto filtrada pelos ids, num grupo "OU" que a URL das listas aceita (até 50 condições). Sem ids, null.
 * @param {string} namePlural @param {string[] | undefined} recordIds
 */
export function qualityRecordsHref(namePlural, recordIds) {
  const ids = (recordIds ?? []).slice(0, 50);
  if (ids.length === 0) return null;
  const parameters = new URLSearchParams();
  parameters.set('filterGroup[operator]', 'OR');
  ids.forEach((id, index) => {
    parameters.set(`filterGroup[filters][${index}][field]`, 'id');
    parameters.set(`filterGroup[filters][${index}][op]`, 'IS');
    parameters.set(`filterGroup[filters][${index}][value]`, id);
  });
  return `/objects/${namePlural}?${parameters.toString()}`;
}

/**
 * Famílias dos leads sem CEP ou com CEP inválido (CEP na família, pacote 1.12.0): a lista de famílias filtrada, com as
 * colunas CEP, Bairro e Cidade editáveis ali mesmo. Os leads em si abrem pelo clique no número (contrato 07, seção 10).
 * @param {string[] | undefined} householdIds
 */
export const qualityHouseholdsHref = (householdIds) => qualityRecordsHref('households', householdIds);

/**
 * O que o rodapé da qualidade mostra para corrigir "sem CEP" ou "inválido" (contrato 07, seção 9.9.9): o link das
 * famílias, quando o papel abre famílias e há família, e quantos desses leads não têm família. Esses se corrigem
 * vinculando uma família no card, e o texto fica visível (não só na dica do link, que o celular não mostra), também
 * quando nenhum lead do grupo tem família. Sem nada a dizer, null.
 * @param {{ count: number, ids: string[], leadsWithoutHousehold: number } | null | undefined} group
 * @param {boolean} canOpenHouseholds
 * @returns {{ householdsHref: string | null, householdsShown: number, householdsCount: number, leadsWithoutHousehold: number } | null}
 */
export function geoQualityFixes(group, canOpenHouseholds) {
  if (!group) return null;
  const householdsHref = canOpenHouseholds ? qualityHouseholdsHref(group.ids) : null;
  const leadsWithoutHousehold = Math.max(0, Number(group.leadsWithoutHousehold) || 0);
  if (!householdsHref && leadsWithoutHousehold === 0) return null;
  return { householdsHref, householdsShown: group.ids?.length ?? 0, householdsCount: group.count, leadsWithoutHousehold };
}

/**
 * Linhas da planilha: um bloco por nível (UF, município, bairro), com as cinco medidas.
 * @param {GeoMapData} data @param {{ level: (level: GeoLevel) => string, unnamed: string, stateOf: (code: string) => string }} labels
 * @returns {Array<Array<string | number | null>>}
 */
export function geoCsvRows(data, labels) {
  const row = (/** @type {GeoLevel} */ level, /** @type {string} */ name, /** @type {string} */ parent, /** @type {GeoCounts} */ counts) => [
    labels.level(level), name, parent, counts.leads, counts.withVisit, counts.enrollments,
    geoMetricValue(counts, 'VISIT_RATE'), geoMetricValue(counts, 'CONVERSION_RATE'),
  ];
  const cityLabel = new Map(data.cities.map((city) => [city.code, city.label]));
  return [
    ...data.states.map((state) => row('STATES', state.label, '', state)),
    ...data.cities.map((city) => row('CITIES', city.label, labels.stateOf(city.stateCode), city)),
    ...data.neighborhoods.map((neighborhood) => row('NEIGHBORHOODS', neighborhood.label ?? labels.unnamed, cityLabel.get(neighborhood.cityCode) ?? '', neighborhood)),
  ];
}

export { isRateMetric };
